import { format } from 'date-fns';
import { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useMatch } from 'react-router-dom';

import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { getTinyAccountById } from 'store/account';
import {
  getKitById,
  getKitsForTinyAccountId,
  getLatestKitForTinyAccountId,
  getResultsReadyKitForTinyAccountId,
} from 'store/kits';
import { getKits } from 'store/shared';
import { getImpersonateId } from 'store/user';

import {
  kitResultsViewable,
  sortKitsByResultsViewableOrLatest,
} from '@repo/utils';
import APImage from 'assets/images/action-card.jpg';
import {
  Card,
  ConsultWidget,
  ContainerSizes,
  Header,
  Icon,
  OrderKitButton,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import VersionPill from 'components/VersionPill/VersionPill';
import { parseDate } from 'helpers';
import EmptyActionPlan from 'views/ActionPlan/EmptyActionPlan';
import ActionPlanCategories from './ActionPlanCategories';
import { ActionPlanDetail } from './ActionPlanDetail';
import ActionPlanDisclaimer from './ActionPlanDisclaimer';
import { ActionRelatedInsights } from './ActionRelatedInsights/ActionRelatedInsights';
import PartnerRecsCard from './PartnerRecsCard/PartnerRecsCard';
import PendingResultsBanner from './PendingResultsBanner';
import PractitionerNote from './PractitionerNote/PractitionerNote';

import { TinyAIPromptEntryPoint } from 'components/TinyAI/TinyAIPromptEntryPoint';
import styles from './ActionPlan.module.scss';

const BlogCard = () => {
  return (
    <Card
      variant='wrapper'
      clickable={{
        text: `How to Use Your Action Plan`,
        href: `https://www.tinyhealth.com/blog/a-guide-to-making-the-most-of-your-microbiome-action-plan`,
        variety: 'Card',
        isExternal: true,
      }}
    >
      <div className={styles.actionPlanCard}>
        <div className={styles.actionPlanCardContent}>
          <div className={styles.actionPlanCardHeader}>
            <Typography variant='heading-s'>
              How to Use Your Action Plan
            </Typography>
            <Icon name='help' size='m' />
          </div>
          <Typography variant='body-s' serif>
            If you’ve taken one of our gut or vaginal tests, you’re...
          </Typography>
        </div>
        <div className={styles.actionPlanImageWrapper}>
          <img src={APImage} alt='Action Plan Information' />
        </div>
      </div>
    </Card>
  );
};

export const ActionPlan = () => {
  const resultMatch = useMatch<'kitId', string>('/results/:kitId');
  const actionPlanMatch = useMatch<'kitId', string>('/action-plan/:kitId');
  const impersonateId = useSelector(getImpersonateId);
  const kits = useSelector(getKits);
  const defaultKitId = sortKitsByResultsViewableOrLatest(kits)[0]?.id;
  const currentKitId =
    resultMatch?.params?.kitId || actionPlanMatch?.params?.kitId;
  const currentKit = useSelector(getKitById(currentKitId));
  const currentKitResultsAreViewable = kitResultsViewable(
    currentKit,
    !!impersonateId,
  );
  const currentTinyAccountId = currentKit?.tinyaccount_id ?? null;
  const currentTinyAccount = useSelector(
    getTinyAccountById(currentTinyAccountId),
  );

  // Get the latest kit (by sample date) and results-ready kit for this tinyaccount
  const latestKit = useSelector(
    getLatestKitForTinyAccountId(currentTinyAccountId),
  );
  const resultsReadyKit = useSelector(
    getResultsReadyKitForTinyAccountId(currentTinyAccountId),
  );
  const kitsForAccount = useSelector(
    getKitsForTinyAccountId(currentTinyAccountId),
  );

  const hasOtherKitsWithResults = kitsForAccount.some(
    kit => kit.id !== currentKitId && kitResultsViewable(kit, !!impersonateId),
  );

  const resultsReadyKitId = resultsReadyKit?.id;

  // Show banner when viewing a previous kit's AP because the latest kit doesn't have results yet
  const showPendingBanner =
    currentKitResultsAreViewable &&
    latestKit &&
    currentKitId !== latestKit.id &&
    !kitResultsViewable(latestKit, !!impersonateId);

  const latestKitDateFormatted = latestKit?.sample_date
    ? format(parseDate(latestKit.sample_date), 'MMMM d, yyyy')
    : undefined;
  const currentKitDateFormatted = currentKit?.sample_date
    ? format(parseDate(currentKit.sample_date), 'MMMM d, yyyy')
    : undefined;

  let NextAction = null;
  if (!resultMatch && !currentKitId && defaultKitId) {
    // By doing this we avoid unnecessary rendering.
    return <Navigate to={`/action-plan/${defaultKitId}`} replace />;
  } else {
    NextAction = (
      <EmptyActionPlan
        hasOtherKitsWithResults={hasOtherKitsWithResults}
        latestKitDate={latestKitDateFormatted}
        resultsReadyKitId={resultsReadyKitId}
      />
    );
  }

  // Extract the condition to show action plan content vs NextAction
  const showActionPlanContent =
    currentKitResultsAreViewable && !!currentKit?.date_survey_completed;

  return (
    <section id='action_plan'>
      <Header
        type='full'
        title={`${currentTinyAccount?.first_name}'s Action Plan`}
      />
      <PageContainer size={ContainerSizes.MAIN}>
        <TwoColumnGrid>
          <TwoColumnGrid.Wide>
            <div className={styles.actionPlanWrap}>
              {showPendingBanner &&
                latestKitDateFormatted &&
                currentKitDateFormatted && (
                  <PendingResultsBanner
                    latestKitDate={latestKitDateFormatted}
                    currentKitDate={currentKitDateFormatted}
                  />
                )}
              <Suspense fallback={<Spinfinity />}>
                {showActionPlanContent ? (
                  <div className='flex flex-column gap-3'>
                    <div className={'hide-on-desktop mb-3'}>
                      <PractitionerNote />
                    </div>
                    <PartnerRecsCard className={'hide-on-desktop'} />
                    <ConsultWidget title='Need help with your action plan?' />
                    <TinyAIPromptEntryPoint
                      location='action_plan_chatbot'
                      variant='banner'
                    />
                    <Suspense fallback={<Spinfinity />}>
                      <ActionPlanCategories kitId={currentKitId} />
                      {currentKit?.sci_initial_version && (
                        <div className='flex gap-2 mt-3'>
                          <Typography variant='label' className='my-auto'>
                            System version
                          </Typography>
                          <VersionPill shadow />
                        </div>
                      )}
                    </Suspense>
                    {currentKit && !!resultMatch && (
                      <OrderKitButton classNames={'pt-4'}>
                        Order your next kit
                      </OrderKitButton>
                    )}
                    <ActionPlanDisclaimer />
                  </div>
                ) : (
                  <Suspense fallback={<Spinfinity />}>{NextAction}</Suspense>
                )}
              </Suspense>
            </div>
          </TwoColumnGrid.Wide>
          <TwoColumnGrid.Slim isSticky>
            <div className={styles.sideColumn}>
              {showActionPlanContent && (
                <div className={'hide-on-mobile'}>
                  <PractitionerNote />
                </div>
              )}
              <BlogCard />
              <PartnerRecsCard className={'hide-on-mobile'} />
            </div>
          </TwoColumnGrid.Slim>
        </TwoColumnGrid>
      </PageContainer>
    </section>
  );
};

export const ActionPlanRoutes = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  return (
    <Routes>
      <Route
        path='/action/:actionId/insights'
        element={<ActionRelatedInsights />}
      />
      <Route path='/action/:actionId' element={<ActionPlanDetail />} />{' '}
      <Route
        path={`/action`}
        key='insights'
        element={<Navigate to={`/action-plan/${currentKitId}`} replace />}
      />
      <Route path='' element={<ActionPlan />} />
    </Routes>
  );
};
